import { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Button, Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useAppSelector } from 'store/hook';
import { Filter } from './Filter';
import AssetChart from './AssetChart';
import PartsChart from './PartsChart';
import Stats from './Stats';
import AssetDetails from './AssetDetails';
import PartsTable from './PartsTable';
import { getCategoryStats } from './utils';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import { filterWorkOrders, workOrdersToIgnore } from 'utils/workorders';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const PartsRecommendationView = ({
  enableDarkTheme,
  showBackButton,
  setTabIndex,
}: {
  enableDarkTheme: boolean;
  showBackButton: boolean;
  setTabIndex?: any;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const squaresHandler = new SquaresDashboardHandler();

  const assets = useAppSelector((state) => state.assetReducer.assets);
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const {
    data: rawStateData,
    prevData: rawPrevData,
    healthData,
    metadata,
    isLoading,
  } = useAppSelector((state) => state.squaresPartsRecommendationsReducer);

  const stateData = useMemo(
    () => filterWorkOrders(rawStateData),
    [rawStateData]
  );

  const prevData = useMemo(() => filterWorkOrders(rawPrevData), [rawPrevData]);

  const data = useMemo(() => {
    const bumperIds = assets.map((item: any) => {
      return item.bumperNumber;
    });
    return stateData.filter((item) => bumperIds.includes(item.asset_id));
  }, [stateData]);

  const uniqueAssets = useMemo(() => {
    return [...new Set(data.map((item) => item.asset_id))].filter(
      (item) => !['nan', 'None'].includes(item)
    );
  }, [data]);

  const sortedComponentCategories = useMemo(() => {
    const assetsData = data.filter((item) =>
      uniqueAssets.includes(item.asset_id)
    );

    const allCategoryStats = getCategoryStats(assetsData);
    const allCategories = allCategoryStats.map((item) => item.category);

    // Filter replacement
    const replacementData = assetsData.filter(
      (item) => item.actions_performed === 'REPLACE'
    );

    const replacementDataCategories = getCategoryStats(replacementData).map(
      (item) => item.category
    );

    // lookup
    const sortedSet = new Set(replacementDataCategories);

    // Merging - sorted first, then remaining from original
    const merged = [
      ...replacementDataCategories,
      ...allCategories.filter((cat) => !sortedSet.has(cat)), // O(n)
    ];

    return merged;
  }, [data, uniqueAssets]);

  const topTenComponentCategories = useMemo(() => {
    return sortedComponentCategories.slice(0, 10);
  }, [sortedComponentCategories]);

  let initialStartDate = new Date();
  initialStartDate.setFullYear(initialStartDate.getFullYear() - 1);
  let initialEndDate = new Date();

  const formerSelectedDates: any = sessionStorage.getItem(
    'datePickerSelection'
  );

  if (formerSelectedDates) {
    const dateObjs = JSON.parse(formerSelectedDates);
    initialStartDate = new Date(dateObjs.startDate);
    initialEndDate = new Date(dateObjs.endDate);
  }

  const [selectedAsset, setSelectedAsset] = useState(uniqueAssets);
  const [selectedPart, setSelectedPart] = useState(topTenComponentCategories);
  const [selectedStartDate, setStartDate] = useState(initialStartDate);
  const [selectedEndDate, setEndDate] = useState(initialEndDate);
  const [selectedFrequency] = useState('Monthly');
  const [selectedComponentFromGraph, setSelectedComponentFromGraph] =
    useState('');
  const [sortType, setSortType] = useState<'cost' | 'count'>('cost');

  const [isSelected, setIsSelected] = useState(
    formerSelectedDates ? 'Custom' : '1Y'
  );

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);

    sessionStorage.setItem(
      'datePickerSelection',
      JSON.stringify({
        startDate,
        endDate,
      })
    );
  };

  useEffect(() => {
    setSelectedAsset(uniqueAssets);
  }, [uniqueAssets]);

  useEffect(() => {
    setSelectedPart(topTenComponentCategories);
  }, [topTenComponentCategories]);

  useEffect(() => {
    if (accessToken) {
      void squaresHandler.getPartsRecommendations(
        selectedStartDate,
        selectedEndDate
      );
    }
  }, [accessToken]);

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.pageBackground
          : palette.pageBackground,
        padding: '10px',
      }}
    >
      {showBackButton && (
        <Button
          sx={{
            textTransform: 'none',
            border: `1px solid ${palette.primary}`,
            color: palette.textPrimary,
          }}
          onClick={() => {
            setTabIndex(0);
          }}
        >
          {t('cdp_button_back')}
        </Button>
      )}
      {isLoading ? (
        <SingleFadeLoader />
      ) : (
        <>
          <Filter
            assets={uniqueAssets}
            selectedAsset={selectedAsset}
            setSelectedAsset={setSelectedAsset}
            categories={sortedComponentCategories}
            selectedPart={selectedPart}
            setSelectedPart={setSelectedPart}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            handleDateChange={handleDateChange}
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />

          <Stats
            enableDarkTheme={enableDarkTheme}
            data={data}
            prevData={prevData}
            selectedPart={selectedPart}
            selectedAsset={selectedAsset}
          />

          <Typography
            sx={{
              fontSize: '12px',
              color: ThemePalette.typography.grey,
            }}
          >
            {t('components_or_sub_components_are_hidden_when_they_fall_into_')}{' '}
            <strong>{workOrdersToIgnore.join(', ')}</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item lg={9} sm={12} md={9} xs={12}>
              <AssetChart
                enableDarkTheme={enableDarkTheme}
                data={data}
                selectedAssets={selectedAsset}
                selectedPart={selectedPart}
                selectedYAxisCategory={selectedComponentFromGraph}
                setSelectedYAxisCategory={setSelectedComponentFromGraph}
                sortType={sortType}
                setSortType={setSortType}
              />
            </Grid>
            <Grid item lg={3} sm={12} md={3} xs={12}>
              <AssetDetails
                data={data}
                selectedPart={selectedPart}
                selectedAsset={selectedAsset}
                selectedYAxisCategory={selectedComponentFromGraph}
                sortType={sortType}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              marginTop: '20px',
              backgroundColor: enableDarkTheme
                ? palette.boxBackground
                : palette.toolBarBackground,
              borderRadius: '5px',
              paddingTop: '20px',
              paddingBottom: '20px',
            }}
          >
            <PartsChart
              data={data}
              selectedPart={selectedPart}
              selectedFrequency={selectedFrequency}
              selectedAssets={selectedAsset}
            />
          </Box>

          <PartsTable
            enableDarkTheme={enableDarkTheme}
            data={data}
            healthData={healthData}
            selectedPart={selectedPart}
            selectedAsset={selectedAsset}
            metadata={metadata}
          />
        </>
      )}
    </Box>
  );
};
